import {
	CarouselBackgroundImage,
	CarouselBackgroundLazyImageContainer,
	MultiLayerCarouselButton,
	MultiLayerCarouselDescription,
	CarouselForegroundImage,
	CarouselForegroundImageContainer,
	CarouselOverlay,
	MultiLayerCarouselTitle
} from '@comeon/ui/src/carousel/base.tsx';
import { MultiLayerCarouselItem } from '@comeon/ui';
import LazyLoadImage from '~/components/utilities/lazyload-image.js';
import { getSmallestSrcFromSrcSet } from '~/components/utilities/image-format';
import { BiggerContainer } from '../layout/mediaquery';

const sizes = {
	foreground: '(max-width: 375px) 300px, (max-width: 768px) 640px, (max-width: 1028px) 1080px, 1080w',
	background: '(max-width: 375px) 420px, (max-width: 768px) 860px, (max-width: 1028px) 1080px, 1920w'
};

/**
 * Renders a multilayer carousel item with optional background and foreground images,
 * header text, message text, and a button. Supports lazy loading and responsive image sizes.
 *
 * @component
 * @param {Object} props - Carousel item props.
 * @param {string} [props.id] - Unique identifier for the carousel item.
 * @param {number} [props.index] - Index of the carousel item in the list.
 * @param {Object} [props.backgroundImage] - Responsive background image configuration.
 * @param {string} [props.backgroundImageURL] - Fallback background image URL.
 * @param {Object} [props.foregroundImage] - Responsive foreground image configuration.
 * @param {string} [props.foregroundImageURL] - Fallback foreground image URL.
 * @param {boolean} [props.externalWindow=false] - Whether the link should open in a new window.
 * @param {string} [props.buttonText] - Text for the action button.
 * @param {string} [props.headerText] - Title text for the carousel item.
 * @param {string} [props.messageText] - Description text.
 * @param {string} [props.link] - URL to navigate when the carousel item is clicked.
 * @param {Function} [props.carouselItemClick=()=>{}] - Callback for item click events.
 * @returns {JSX.Element}
 */
const CarouselItem = ({
	id,
	index,
	backgroundImage,
	foregroundImage,
	backgroundImageURL,
	foregroundImageURL,
	externalWindow = false,
	buttonText,
	headerText,
	messageText,
	link,
	carouselItemClick = () => {}
}) => {
	const isPictureCompatible = !!(backgroundImage && foregroundImage);

	const body = (
		<>
			{isPictureCompatible ? (
				<CarouselBackgroundLazyImageContainer>
					<LazyLoadImage
						alt={link}
						src={getSmallestSrcFromSrcSet(backgroundImage.srcSet)}
						srcSet={backgroundImage.srcSet}
						sizes={sizes.background}
						pictureElementData={backgroundImage.sources}
						loading="eager"
					/>
				</CarouselBackgroundLazyImageContainer>
			) : (
				<CarouselBackgroundImage src={backgroundImageURL} alt={link} />
			)}
			<CarouselForegroundImageContainer>
				{isPictureCompatible ? (
					<LazyLoadImage
						alt={link}
						src={foregroundImage.srcSmall}
						srcSet={foregroundImage.srcSet}
						sizes={sizes.foreground}
						pictureElementData={foregroundImage.sources}
						loading="eager"
					/>
				) : (
					<CarouselForegroundImage src={foregroundImageURL} alt={link} />
				)}
			</CarouselForegroundImageContainer>
			<CarouselOverlay />
			{headerText && <MultiLayerCarouselTitle dataAt="carousel-title">{headerText}</MultiLayerCarouselTitle>}
			{messageText && (
				<MultiLayerCarouselDescription
					className="carousel-multilayer-description"
					dataAt="carousel-description"
				>
					{messageText}
				</MultiLayerCarouselDescription>
			)}
			{buttonText && (
				<BiggerContainer>
					<MultiLayerCarouselButton dataAt="carousel-button">{buttonText}</MultiLayerCarouselButton>
				</BiggerContainer>
			)}
		</>
	);

	return (
		<MultiLayerCarouselItem
			carouselItemClick={carouselItemClick}
			link={link}
			externalWindow={externalWindow}
			headerText={headerText}
			id={id}
			index={index}
		>
			{body}
		</MultiLayerCarouselItem>
	);
};

export default CarouselItem;
